'use client';

import React from 'react';
import { Users, Building, Folder } from 'lucide-react';
import * as SubframeUtils from '../utils';
import { Avatar } from './Avatar';
import { Badge } from './Badge';

interface WorkspaceInfo {
  label: string;
  value: string;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: 'members' | 'departments' | 'categories';
  active?: boolean;
  onClick?: () => void;
}

interface WorkspaceNavigationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  workspaceName: string;
  workspaceImage?: string;
  badge?: {
    text: string;
    variant?: 'brand' | 'neutral' | 'success' | 'warning';
  };
  infos: WorkspaceInfo[];
  navigationItems: NavigationItem[];
  className?: string;
}

const iconMap = {
  members: Users,
  departments: Building,
  categories: Folder,
};

const NavigationItemComponent = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    icon: 'members' | 'departments' | 'categories';
    active?: boolean;
    onClick?: () => void;
  }
>(function NavigationItemComponent({ label, icon, active = false, onClick }, ref) {
  const IconComponent = iconMap[icon];

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={SubframeUtils.twClassNames(
        'flex w-full items-center gap-2 rounded-lg border-none px-3 py-3 text-left transition-all duration-200',
        'focus-visible:ring-brand-600/10 focus:outline-none focus-visible:ring-4',
        {
          'bg-[#f5f5f5] text-[#20154c]': active,
          'bg-white text-[#535862] hover:bg-neutral-50': !active,
        },
      )}
    >
      <IconComponent
        className={SubframeUtils.twClassNames('h-5 w-5 flex-shrink-0', {
          'text-[#20154c]': active,
          'text-[#535862]': !active,
        })}
      />
      <span
        className={SubframeUtils.twClassNames('text-base font-medium', {
          'text-[#20154c]': active,
          'text-[#535862]': !active,
        })}
      >
        {label}
      </span>
    </button>
  );
});

NavigationItemComponent.displayName = 'NavigationItem';

export const WorkspaceNavigationCard = React.forwardRef<
  HTMLDivElement,
  WorkspaceNavigationCardProps
>(function WorkspaceNavigationCard(
  { workspaceName, workspaceImage, badge, infos, navigationItems, className, ...otherProps },
  ref,
) {
  return (
    <div
      ref={ref}
      className={SubframeUtils.twClassNames(
        'flex flex-col gap-5 overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm',
        'w-full',
        className,
      )}
      {...otherProps}
    >
      {/* Header - Workspace Info */}
      <div className="flex items-center gap-4">
        <Avatar size="x-large" image={workspaceImage} variant="brand-gradient">
          {!workspaceImage && workspaceName.substring(0, 1).toLowerCase()}
        </Avatar>
        <div className="flex flex-1 flex-col gap-2">
          <h3 className="text-lg leading-tight font-semibold text-[#181d27]">{workspaceName}</h3>
          {badge && (
            <div className="inline-flex w-fit items-center rounded-md border border-[#b9e6fe] bg-[#f0f9ff] px-2 py-0.5">
              <span className="text-sm font-medium text-[#026aa2]">{badge.text}</span>
            </div>
          )}
        </div>
      </div>

      {/* Separator */}
      <div className="h-px w-full bg-neutral-200" />

      {/* Info Section */}
      <div className="flex flex-col gap-4">
        {infos.map((info, index) => (
          <div key={index} className="flex flex-col gap-2">
            <span className="text-sm text-[#535862]">{info.label}</span>
            <span className="text-sm font-medium text-[#252b37]">{info.value}</span>
          </div>
        ))}
      </div>

      {/* Separator */}
      <div className="h-px w-full bg-neutral-200" />

      {/* Navigation Items */}
      <nav className="flex flex-col gap-3">
        {navigationItems.map((item) => (
          <NavigationItemComponent
            key={item.id}
            label={item.label}
            icon={item.icon}
            active={item.active}
            onClick={item.onClick}
          />
        ))}
      </nav>
    </div>
  );
});

const NavigationItem = NavigationItemComponent;

WorkspaceNavigationCard.displayName = 'WorkspaceNavigationCard';
